/**
 * RenameOrgDialog — change an organization's DISPLAY NAME.
 *
 * Wire: `PATCH /api/platform/orgs/:orgId` with `{name}` (platform-admin only,
 * audited as `org.update`). The slug is never sent and cannot change.
 *
 * First consumer is the go-live plan's **P-TAG** phase
 * (`ai-docs/prod-beta-golive-plan.md` §5) — prefixing every currently-live prod
 * org with `[TEST] ` so the real beta firms are unmistakable beside the mock
 * data. Hence the one-click prefix toggle: P-TAG is a list of five orgs, and
 * hand-typing a bracketed prefix five times invites exactly the typo
 * (`[TEST]Foo`, `[Test] Foo`) that makes the tag useless as a filter.
 *
 * Two safety properties, both deliberate — this runs against PRODUCTION orgs:
 *
 *  1. **Confirm by SLUG.** The operator retypes the org's slug to arm the
 *     button. Names are duplicable and the name is the thing being replaced;
 *     the slug is the stable unique identity, so it is what proves "I know
 *     which org I am on". Same rationale as `PermanentDeleteOrgDialog`.
 *  2. **Self-verified save.** We compare the name the server echoes against
 *     the one we asked for and report a silent no-op as a FAILURE. See
 *     `classifyRenameSave` for why a 200 is not evidence here.
 *
 * Gate + outcome logic lives in `lib/org-rename.ts` (pure, unit-tested).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Pencil, TriangleAlert } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePlatformApi } from "@/hooks/usePlatformApi";
import { PlatformApiError } from "@/lib/platform-api";
import {
  ORG_NAME_MAX,
  classifyRenameSave,
  describeRenameOutcome,
  evaluateRenameGate,
  hasTestPrefix,
  withTestPrefix,
  withoutTestPrefix,
} from "@/lib/org-rename";

export interface RenameOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Non-null while the dialog is open. */
  org: { orgId: string; name: string; slug: string } | null;
  /** Called after a VERIFIED rename — parent refreshes from the server. */
  onRenamed: (next: { orgId: string; name: string }) => void;
}

export function RenameOrgDialog({
  open,
  onOpenChange,
  org,
  onRenamed,
}: RenameOrgDialogProps) {
  const api = usePlatformApi();

  const [name, setName] = useState("");
  const [typedConfirm, setTypedConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgId = org?.orgId ?? null;

  // The org NAME is read through a ref rather than taken as an effect
  // dependency. Callers build this prop as a fresh object literal per render
  // (`OrgUsers.tsx` does), so depending on `org` — or on `org.name` — re-runs
  // the reset on every unrelated parent render and silently wipes whatever
  // the operator has typed. Pinned by "operator input survives parent
  // re-renders" in the component test.
  const orgNameRef = useRef(org?.name ?? "");
  orgNameRef.current = org?.name ?? "";

  /**
   * Monotonic token for the open/target generation. Bumped whenever the
   * dialog opens or is pointed at a different org; a save compares it after
   * its await so a slow response can never report against the org that
   * replaced it. Same guard the git browser and the delete-census use — in
   * this lane that mistake once authorised acting on the WRONG record.
   */
  const runRef = useRef(0);

  // Reset every field on (re)open, or when retargeted at another org — a
  // previous attempt's typed text must never leak into a fresh run, least of
  // all the confirm phrase.
  useEffect(() => {
    if (!open || !orgId) return;
    runRef.current += 1;
    setName(orgNameRef.current);
    setTypedConfirm("");
    setError(null);
    setSaving(false);
  }, [open, orgId]);

  const gate = org
    ? evaluateRenameGate({
        currentName: org.name,
        nextName: name,
        slug: org.slug,
        typedConfirm,
        busy: saving,
      })
    : ({ canSubmit: false, reason: "No organization selected." } as const);

  const handleSave = useCallback(async () => {
    if (!org || !gate.canSubmit) return;
    const run = runRef.current;
    const target = org.orgId;
    const previousName = org.name;
    setSaving(true);
    setError(null);
    try {
      const res = await api.updateOrg(target, { name: gate.nextName });
      // Closed, reopened, or pointed at another org while this was in flight:
      // this answer describes an org the operator is no longer looking at.
      if (runRef.current !== run) return;

      // Never trust the 200 — see classifyRenameSave.
      const outcome = classifyRenameSave(gate.nextName, previousName, res.data);
      const problem = describeRenameOutcome(outcome);
      if (problem) {
        setError(problem);
        return;
      }

      onRenamed({ orgId: target, name: gate.nextName });
      onOpenChange(false);
    } catch (err) {
      if (runRef.current !== run) return;
      const code = err instanceof PlatformApiError ? err.code : null;
      const msg = err instanceof Error ? err.message : "Rename failed";
      setError(code ? `${code}: ${msg}` : msg);
    } finally {
      if (runRef.current === run) setSaving(false);
    }
  }, [api, org, gate, onRenamed, onOpenChange]);

  if (!org) return null;

  const tagged = hasTestPrefix(name);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg space-y-4" data-testid="rename-org-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg text-foreground">
            <Pencil className="h-4 w-4" />
            Rename organization
          </DialogTitle>
          <DialogDescription>
            Changes the display name only. The slug{" "}
            <strong className="font-mono text-foreground">{org.slug}</strong> is
            storage-path-bearing and never changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1">
          <label htmlFor="rename-org-name" className="text-xs font-medium text-muted">
            Display name (max {ORG_NAME_MAX} characters):
          </label>
          <input
            id="rename-org-name"
            type="text"
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            data-testid="rename-org-name"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
          />
          <div className="flex items-center justify-between gap-2 pt-0.5">
            <button
              type="button"
              onClick={() => setName(tagged ? withoutTestPrefix(name) : withTestPrefix(name))}
              disabled={saving}
              data-testid="rename-org-test-prefix"
              className="rounded border border-border px-1.5 py-0.5 text-[11px] font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
            >
              {tagged ? "Remove [TEST] prefix" : "Add [TEST] prefix"}
            </button>
            <span className="text-[11px] text-faint">
              was: <span className="font-medium text-muted">{org.name}</span>
            </span>
          </div>
        </div>

        {/* What a rename actually propagates to. Traced, not assumed — see the
            header comment in lib/org-rename.ts for the file:line evidence.
            An operator renaming a LIVE org needs this before pressing save. */}
        <div className="rounded-md border border-border bg-panel-2 p-3 text-xs text-muted">
          <p className="mb-1 font-medium text-foreground">This name is not only cosmetic:</p>
          <ul className="list-disc space-y-0.5 pl-4">
            <li>
              It resolves into the agent&apos;s system prompt (
              <code className="font-mono">{"{{org.name}}"}</code>) on the next turn.
            </li>
            <li>Invitation emails use it at send time, in the subject and the body.</li>
            <li>
              Mailboxes already provisioned keep the <strong>old</strong> name —
              nothing rewrites an existing sender identity.
            </li>
            <li>Clerk is untouched; no org name over there to drift from.</li>
          </ul>
        </div>

        <div className="space-y-1">
          <label htmlFor="rename-org-confirm" className="text-xs font-medium text-muted">
            Type the slug{" "}
            <span className="font-mono font-semibold text-foreground">{org.slug}</span>{" "}
            to confirm which org you are renaming:
          </label>
          <input
            id="rename-org-confirm"
            type="text"
            autoComplete="off"
            value={typedConfirm}
            onChange={(e) => setTypedConfirm(e.target.value)}
            placeholder={org.slug}
            disabled={saving}
            data-testid="rename-org-confirm"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 font-mono text-sm text-foreground outline-none placeholder:text-faint focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
          />
        </div>

        {error && (
          <div
            className="flex items-start gap-2 rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
            data-testid="rename-org-error"
          >
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="rename-org-cancel"
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!gate.canSubmit}
            title={gate.canSubmit ? undefined : gate.reason}
            onClick={() => void handleSave()}
            data-testid="rename-org-save"
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {saving ? "Renaming…" : "Rename"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
